import { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ReactTags } from 'react-tag-autocomplete';
import * as REPORT from '../../constants/reports';
import {
  useAddReportMutation,
  useGetOrganizationsQuery,
  useGetWorkspacesByOrganizationQuery,
  useGetAvailableReportCodesQuery
} from '../../services/portal';

const CreateReport = () => {
  let navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  const [selectedOrganization, setSelectedOrganization] = useState(2);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [selectedWorkspaceCode, setSelectedWorkspaceCode] = useState(null);
  const [reportType, setReportType] = useState('');
  const [availableReportCodes, setAvailableReportCodes] = useState([]);
  const [selected, setSelected] = useState([]);

  const [isRowLevelSecurity, setIsRowLevelSecurity] = useState(false);
  const [isSSASSecurity, setIsSSASSecurity] = useState(false);
  const [isSSASAndRLS, setIsSSASAndRLS] = useState(false);

  const { data: organizations } = useGetOrganizationsQuery({ forceRefetch: true });
  const { data: workspaces } = useGetWorkspacesByOrganizationQuery(selectedOrganization, {
    forceRefetch: true
  });
  const { data: reportCodes } = useGetAvailableReportCodesQuery(selectedWorkspaceCode, {
    forceRefetch: true
  });

  const [addReport, { isLoading: isUpdating }] = useAddReportMutation();

  useEffect(() => {
    if (organizations) {
      setValue('organization', organizations.organizations[0].organization_id);
      setSelectedOrganization(organizations.organizations[0].organization_id);
    }
  }, [organizations]);

  useEffect(() => {
    if (workspaces && workspaces.workspaces.length > 0) {
      setValue('workspace', workspaces.workspaces[0].workspace_id);
      setSelectedWorkspace(workspaces.workspaces[0].workspace_id);
      setSelectedWorkspaceCode(workspaces.workspaces[0].workspace_code);
    }
  }, [workspaces]);

  useEffect(() => {
    if (reportCodes && reportCodes.reportCodes) {
      setAvailableReportCodes(reportCodes.reportCodes);
    }
  }, [reportCodes]);

  const handleOrganizationChange = (e) => {
    const selectedOrganizationId = e.target.value;
    setSelectedOrganization(selectedOrganizationId);
    setSelectedWorkspace(null);
    setSelectedWorkspaceCode(null);
  };

  const handleWorkspaceChange = (e) => {
    const selectedWorkspaceId = e.target.value;
    const selectedWorkspaceData = workspaces?.workspaces.find(
      (w) => w.workspace_id == selectedWorkspaceId
    );
    if (selectedWorkspaceData) {
      setSelectedWorkspace(selectedWorkspaceId);
      setSelectedWorkspaceCode(selectedWorkspaceData.workspace_code);
    }
  };

  const handleCodeChange = (e) => {
    const selectedCode = e.target.value;
    const selectedIndex = availableReportCodes.indexOf(selectedCode);
    if (selectedIndex !== -1) {
      const selectedReportType = reportCodes.reportType[selectedIndex];
      // Normalize the report type to match constants
      const normalizedType = selectedReportType === 'PaginatedReport' ? REPORT.TYPE.PAGINATED : REPORT.TYPE.REPORT;
      setReportType(normalizedType);
    }
  };

  const onSubmit = (formData) => {
    const selectedIndex = availableReportCodes.indexOf(formData.code);
    let selectedReportType = reportCodes.reportType[selectedIndex];
    selectedReportType = selectedReportType === 'PowerBIReport' ? 'Report' : 'Paginated Report';

    addReport({
      report_name: formData.name,
      report_shortname: 'XX',
      report_code: formData.code,
      report_description: formData.description,
      report_type: selectedReportType,
      report_rls: {
        enabled: isRowLevelSecurity,
        roles: reportType === REPORT.TYPE.PAGINATED ? [] : selected.map((s) => s.value)
      },
      report_ssas: {
        enabled: isSSASSecurity,
        username: formData.username
      },
      report_ssasrls: {
        enabled: isSSASAndRLS,
        username: selected.map((s) => s.value)
      },
      workspace: {
        workspace_id: formData.workspace
      },
      organization: {
        organization_id: formData.organization
      }
    })
      .unwrap()
      .then(() => navigate('/reports'))
      .catch((error) => console.error('rejected', error));
  };

  const onAdd = useCallback((newTag) => setSelected([...selected, newTag]), [selected]);
  const onDelete = useCallback((tagIndex) => setSelected(selected.filter((_, i) => i !== tagIndex)), [selected]);
  const handleCancel = () => navigate('/reports');

  return (
    <div className="page">
      <div className="page-inner">
        <header className="page-title-bar">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item active">
                <a href="/reports">
                  <i className="breadcrumb-icon fa fa-angle-left mr-2"></i>
                  Reports
                </a>
              </li>
            </ol>
          </nav>
          <div className="d-flex justify-content-between">
            <h1 className="page-title">New Report</h1>
          </div>
        </header>

        <div className="page-section">
          <div className="card">
            <div className="card-body">
              <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                <h6>Report</h6>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Organization <span className="text-danger">*</span></Form.Label>
                  <select
                    id="organization"
                    className="custom-select"
                    {...register('organization')}
                    onChange={handleOrganizationChange}>
                    {organizations?.organizations.map((item, key) => (
                      <option key={key} value={item.organization_id}>
                        {item.organization_name}
                      </option>
                    ))}
                  </select>
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Workspace <span className="text-danger">*</span></Form.Label>
                  <select
                    id="workspace"
                    className="custom-select"
                    {...register('workspace')}
                    onChange={handleWorkspaceChange}>
                    {workspaces?.workspaces.map((item, key) => (
                      <option key={key} value={item.workspace_id}>
                        {item.workspace_name}
                      </option>
                    ))}
                  </select>
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Report <span className="text-danger">*</span></Form.Label>
                  <select
                    id="code"
                    className="custom-select"
                    {...register('code', { required: true })}
                    onChange={handleCodeChange}>
                    {reportCodes &&
                      Array.isArray(reportCodes.reportNames) &&
                      reportCodes.reportNames
                        .map((name, index) => ({ name, code: reportCodes.reportCodes[index] }))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(({ name, code }, index) => (
                          <option key={index} value={code}>
                            {name}
                          </option>
                        ))}
                  </select>
                  {errors?.code && errors.code.type === 'required' && (
                    <p className="validate-feedback-invalid">Report code is required.</p>
                  )}
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Report Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    id="name"
                    placeholder="Report name"
                    {...register('name', { minLength: 3, required: true })}
                  />
                  {errors?.name && errors.name.type === 'required' && (
                    <p className="validate-feedback-invalid">Report name is required.</p>
                  )}
                  {errors?.name && errors.name.type === 'minLength' && (
                    <p className="validate-feedback-invalid">At least 3 characters.</p>
                  )}
                </Form.Group>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    id="description"
                    placeholder="Description"
                    {...register('description')}
                  />
                </Form.Group>

                <h6>Security <span className="text-danger">*</span></h6>

                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Check
                    bsPrefix="custom-control"
                    label="None"
                    name="reportSecurityType"
                    type="radio"
                    id="none"
                    checked={!(isRowLevelSecurity || isSSASSecurity || isSSASAndRLS)}
                    onChange={() => {
                      setIsRowLevelSecurity(false);
                      setIsSSASSecurity(false);
                      setIsSSASAndRLS(false);
                    }}
                  />

                  <Form.Check
                    bsPrefix="custom-control"
                    label="Row-Level Security"
                    name="reportSecurityType"
                    type="radio"
                    id="rls"
                    checked={isRowLevelSecurity}
                    onChange={() => {
                      setIsRowLevelSecurity(true);
                      setIsSSASSecurity(false);
                      setIsSSASAndRLS(false);
                    }}
                  />
                  {isRowLevelSecurity && (
                    <Form.Group className="pl-3 pt-1">
                      <ReactTags
                        allowNew
                        ariaDescribedBy="Report roles"
                        closeOnSelect
                        id="report-roles"
                        labelText="Enter new roles"
                        placeholderText="Add role(s)"
                        onAdd={onAdd}
                        onDelete={onDelete}
                        selected={selected}
                        suggestions={[]}
                      />
                      <small className="text-muted">Valid role(s) for effective identity.</small>
                    </Form.Group>
                  )}

                  <Form.Check
                    bsPrefix="custom-control"
                    label="SQL Server Analysis Services (SSAS)"
                    name="reportSecurityType"
                    type="radio"
                    id="ssas"
                    checked={isSSASSecurity}
                    onChange={() => {
                      setIsRowLevelSecurity(false);
                      setIsSSASSecurity(true);
                      setIsSSASAndRLS(false);
                    }}
                  />
                  {isSSASSecurity && (
                    <Form.Group className="pl-3 pt-1">
                      <Form.Control
                        type="text"
                        id="username"
                        placeholder="Username"
                        {...register('username')}
                      />
                      <small className="text-muted">Username recognized by SSAS.</small>
                    </Form.Group>
                  )}

                  <Form.Check
                    bsPrefix="custom-control"
                    label="SQL Server Analysis Services + RLS"
                    name="reportSecurityType"
                    type="radio"
                    id="ssasrls"
                    checked={isSSASAndRLS}
                    onChange={() => {
                      setIsRowLevelSecurity(false);
                      setIsSSASSecurity(false);
                      setIsSSASAndRLS(true);
                    }}
                  />
                  {isSSASAndRLS && (
                    <>
                      <Form.Group className="pl-3 pt-1">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          id="ssasrls-username"
                          placeholder="Username"
                          {...register('username')}
                        />
                      </Form.Group>
                      <Form.Group className="pl-3 pt-1">
                        <Form.Label>Roles</Form.Label>
                        <ReactTags
                          allowNew
                          ariaDescribedBy="SSAS RLS Roles"
                          closeOnSelect
                          id="ssas-rls-roles"
                          labelText="Enter new roles"
                          placeholderText="Add role(s)"
                          onAdd={onAdd}
                          onDelete={onDelete}
                          selected={selected}
                          suggestions={[]}
                        />
                      </Form.Group>
                    </>
                  )}
                </Form.Group>

                <hr className="mb-0 mt-0" />
                <div className="form-actions">
                  <Button variant="primary" disabled={isUpdating} type="submit">
                    Save
                  </Button>
                  <Button variant="secondary" style={{ marginLeft: 10 }} onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReport;
